import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useUebung } from '../useUebung'
import { Ballons } from '../spiel/Ballons'
import { Zahlenfeld } from '../spiel/Zahlenfeld'
import { Maskottchen, type Stimmung } from '../spiel/Maskottchen'
import { Sterne, Konfetti } from '../spiel/Deko'
import { aktiveVerbindung } from '../kopplung/aktiv'
import type { Aufgabe, Rueckmeldung, Rundenergebnis } from '../scheduler/typen'
import { AUFGABEN_PRO_RUNDE, KETTE_AB } from '../config'
import type { Tagesstand } from '../raum/api'
import { reiseStand } from '../raum/reise'
import { naechstesSet } from '../spiel/ballonsets'

export function Uebung() {
  const navigate = useNavigate()
  const verbindung = aktiveVerbindung()
  const alsFernbedienung = verbindung !== null
  const letzte = useRef<{ aufgabe: Aufgabe | null; ergebnis: Rundenergebnis | null }>({
    aufgabe: null,
    ergebnis: null,
  })

  /**
   * Der Spiegel zum Fernseher.
   *
   * Das Tablet schickt fertige Anzeigebefehle, keine Zustaende. Der Fernseher
   * muss dadurch nichts verstehen und kann nichts falsch machen.
   */
  const spiegel = useMemo(() => {
    if (!verbindung) return undefined
    return {
      aufgabeZeigen(aufgabe: Aufgabe) {
        letzte.current.aufgabe = aufgabe
        verbindung.zeigen({
          art: 'aufgabe',
          text: aufgabe.variante.text,
          nummer: aufgabe.nummer,
          gesamt: aufgabe.gesamt,
          antworten: aufgabe.antworten,
        })
      },
      rueckmeldungZeigen(rueck: Rueckmeldung, aufgabe: Aufgabe) {
        verbindung.zeigen({
          art: 'rueckmeldung',
          richtig: rueck.richtig && rueck.zone !== 'hakt',
          loesung: rueck.loesung,
          merksatz: rueck.merksatz,
          text: aufgabe.variante.text,
        })
        if (rueck.ketteGezuendet) {
          verbindung.zeigen({ art: 'kette', laenge: rueck.kette })
        }
      },
      fertigZeigen(ergebnis: Rundenergebnis) {
        letzte.current.ergebnis = ergebnis
        verbindung.zeigen({
          art: 'fertig',
          gewusst: ergebnis.anzahlGewusst,
          gesamt: ergebnis.anzahlAufgaben,
          schnellsteMs: ergebnis.schnellsteMs,
        })
      },
    }
  }, [verbindung])

  const {
    zustand,
    aufgabe,
    rueck,
    ergebnis,
    profil,
    blitz,
    raumstand,
    stoppuhr,
    pause,
    fortschritt,
    sicher,
    antworten,
    weiter,
    neuStarten,
  } = useUebung(spiegel)

  // Wenn der Fernseher neu geladen wurde, fragt er nach dem aktuellen Stand.
  // Das Tablet schickt schlicht noch einmal, was gerade gilt.
  useEffect(() => {
    if (!verbindung) return
    const alt = aktiveVerbindung()
    if (!alt) return
    // Der Handler wird beim Koppeln gesetzt; hier nur den Inhalt nachliefern.
    const nachliefern = () => {
      const a = letzte.current.aufgabe
      if (letzte.current.ergebnis) {
        const e = letzte.current.ergebnis
        verbindung.zeigen({
          art: 'fertig',
          gewusst: e.anzahlGewusst,
          gesamt: e.anzahlAufgaben,
          schnellsteMs: e.schnellsteMs,
        })
      } else if (a) {
        verbindung.zeigen({
          art: 'aufgabe',
          text: a.variante.text,
          nummer: a.nummer,
          gesamt: a.gesamt,
          antworten: a.antworten,
        })
      }
    }
    window.addEventListener('zahlenflug:stand-anfordern', nachliefern)
    return () => window.removeEventListener('zahlenflug:stand-anfordern', nachliefern)
  }, [verbindung])

  const abbrechen = useCallback(() => navigate('/'), [navigate])

  if (zustand === 'laedt') {
    return (
      <div className="buehne">
        <Sterne />
        <div className="mitte">
          <Maskottchen gross />
          <p className="unterzeile">Einen Moment …</p>
        </div>
      </div>
    )
  }

  if (zustand === 'fertig' && ergebnis) {
    return (
      <Abschluss
        ergebnis={ergebnis}
        raumstand={raumstand}
        fortschritt={fortschritt}
        sicher={sicher}
        onNochmal={neuStarten}
        onFertig={abbrechen}
      />
    )
  }

  if (!aufgabe) return null

  const zeigtMerksatz = zustand === 'rueckmeldung' && rueck?.zone === 'hakt'
  const stimmung = stimmungFuer(zustand, rueck)

  return (
    <div className="buehne">
      <Sterne anzahl={25} />

      <div className="kopfzeile">
        <button className="knopf knopf--leise" onClick={abbrechen} type="button">
          Zurück
        </button>
        <div className="fortschritt">
          <div
            className="fortschritt__balken"
            style={{ width: `${((aufgabe.nummer - 1) / AUFGABEN_PRO_RUNDE) * 100}%` }}
          />
        </div>
        <span className="zaehler">
          {aufgabe.nummer} / {aufgabe.gesamt}
        </span>
      </div>

      {rueck && rueck.kette >= KETTE_AB && zustand === 'rueckmeldung' && (
        <div className="kette">{rueck.kette} richtig in Folge!</div>
      )}

      <div className="mitte" style={{ paddingTop: 0 }}>
        {/* Haengt der Fernseher dran, steht die Aufgabe dort. Das Tablet ist
            dann reine Fernbedienung: Sie schaut hoch und tippt nach unten. */}
        {!alsFernbedienung && (
          <h2 className="aufgabe">
            {aufgabe.variante.text} <span className="aufgabe__frage">= ?</span>
          </h2>
        )}

        {aufgabe.format === 'ballons' && aufgabe.antworten ? (
          <Ballons
            antworten={aufgabe.antworten}
            loesung={aufgabe.variante.ergebnis}
            gesperrt={zustand !== 'frage'}
            aufgabenId={`${aufgabe.nummer}-${aufgabe.variante.id}`}
            stoppuhr={stoppuhr}
            setId={profil?.ballonset ?? ''}
            onAntwort={antworten}
          />
        ) : (
          <Zahlenfeld
            aufgabenId={`${aufgabe.nummer}-${aufgabe.variante.id}`}
            stoppuhr={stoppuhr}
            gesperrt={zustand !== 'frage'}
            onAntwort={antworten}
          />
        )}
      </div>

      <Maskottchen stimmung={pause ? 'staunt' : stimmung} />

      {blitz && <div className={`blitz blitz--${blitz}`} />}
      {blitz === 'schnell' && <div className="blitzwort">Blitzschnell!</div>}

      {/* Nach einer Unterbrechung: kurz Bescheid sagen, dass die Aufgabe noch
          steht. Ohne diesen Hinweis wuerde ein Kind denken, sein Tippen sei
          nicht angekommen. */}
      {pause && <div className="pause">Da bist du ja wieder! Diese Aufgabe nochmal.</div>}

      {zeigtMerksatz && rueck && (
        <button
          className="rueck"
          onClick={weiter}
          type="button"
          style={{ border: 'none', font: 'inherit', color: 'inherit' }}
        >
          <Maskottchen stimmung="trost" gross />
          <p className="rueck__loesung">
            {aufgabe.variante.text} = {rueck.loesung}
          </p>
          <p className="rueck__merksatz">{rueck.merksatz}</p>
          <p className="rueck__weiter">Tipp irgendwo hin, dann geht es weiter</p>
        </button>
      )}
    </div>
  )
}

function stimmungFuer(zustand: string, rueck: Rueckmeldung | null): Stimmung {
  if (zustand !== 'rueckmeldung' || !rueck) return 'neutral'
  if (rueck.zone === 'hakt') return 'trost'
  if (rueck.ketteGezuendet) return 'feiert'
  if (rueck.zone === 'gewusst') return 'begeistert'
  return 'staunt'
}

export function Abschluss({
  ergebnis,
  raumstand,
  fortschritt,
  sicher,
  onNochmal,
  onFertig,
}: {
  ergebnis: Rundenergebnis
  raumstand: Tagesstand | null
  fortschritt: string | null
  sicher: number
  onNochmal: () => void
  onFertig: () => void
}) {
  const sekunden = ergebnis.schnellsteMs ? (ergebnis.schnellsteMs / 1000).toFixed(1) : null

  return (
    <div className="buehne">
      <Sterne />
      <Konfetti />

      <div className="mitte">
        <Maskottchen stimmung="feiert" gross />
        <h1 className="titel">{abschlussTitel(ergebnis)}</h1>

        <div className="karten">
          <div className="karte">
            <div className="karte__wert">
              {ergebnis.anzahlGewusst} / {ergebnis.anzahlAufgaben}
            </div>
            <div className="karte__name">sofort gewusst</div>
          </div>

          {sekunden && (
            <div className="karte">
              <div className="karte__wert">{sekunden}s</div>
              <div className="karte__name">deine schnellste Antwort</div>
            </div>
          )}

          <div className="karte">
            <div className="karte__wert">{ergebnis.laengsteKette}</div>
            <div className="karte__name">längste Kette</div>
          </div>
        </div>

        {/* Der Fortschritts-Satz steht ueber allem anderen. Er ist das
            einzige Lob in der App, das eine Zahl nennt, und er ist wahr:
            dieselbe Aufgabe, gemessen an zwei verschiedenen Tagen. */}
        {fortschritt && <p className="fortschritt-satz">{fortschritt}</p>}

        <NaechsteFarbwelt sicher={sicher} />

        {raumstand && <RaumHinweis stand={raumstand} />}

        <div className="zeile">
          <button className="knopf" onClick={onFertig} type="button">
            Fertig
          </button>
          <button className="knopf knopf--leise" onClick={onNochmal} type="button">
            Noch eine Runde
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Was der Freundinnen-Raum zur gerade beendeten Runde meldet.
 *
 * Erscheint nur, wenn Lina in einem Raum ist, und nie als Fehlermeldung: Ist
 * der Server nicht erreichbar, bleibt der Abschnitt einfach weg. Die Runde
 * ist trotzdem gezaehlt und wird beim naechsten Mal nachgereicht.
 */
function RaumHinweis({ stand }: { stand: Tagesstand }) {
  const reise = reiseStand(stand.reise.rundenGesamt)
  const geradeAngekommen = stand.neuGeschafft && reise.anteil === 0 && reise.stationIndex > 0

  return (
    <div className="raumkachel" style={{ marginTop: '2vmin' }}>
      <div className="raumkachel__titel">
        {geradeAngekommen
          ? `Ihr seid in ${reise.station.name} angekommen!`
          : stand.neuGeschafft
            ? 'Du hast ein Feld aufgedeckt!'
            : 'Heute warst du schon dran'}
      </div>

      <div className="raumkachel__zeile">
        {geradeAngekommen
          ? reise.station.info
          : reise.amZiel
            ? 'Ihr habt das Ziel erreicht.'
            : `Noch ${reise.nochRunden === 1 ? 'eine Runde' : `${reise.nochRunden} Runden`} bis ${reise.naechste?.name}.`}
      </div>

      <div className="raumkachel__zeile">
        {stand.raetsel.vollAufgedeckt
          ? `Aufgeloest: ${stand.raetsel.pointe}`
          : `Heute haben ${stand.raetsel.geschafftHeute} von ${stand.raetsel.mitgliederAnzahl} geuebt.`}
      </div>

      <Link className="knopf knopf--leise" to="/raum" style={{ marginTop: '2vmin', display: 'inline-block' }}>
        Zum Raum
      </Link>
    </div>
  )
}

/**
 * Hinweis auf die naechste Ballon-Farbwelt.
 *
 * Erscheint nur, wenn sie nah genug ist. Ein Ziel, das noch dreissig Karten
 * entfernt liegt, motiviert niemanden; eines, das noch zwei entfernt ist,
 * sehr. Deshalb schweigt der Hinweis die meiste Zeit.
 */
function NaechsteFarbwelt({ sicher }: { sicher: number }) {
  const naechste = naechstesSet(sicher)
  if (!naechste) return null
  if (naechste.nochKarten > 4) return null

  return (
    <p className="farbwelt-hinweis">
      Noch{' '}
      {naechste.nochKarten === 1
        ? 'eine Aufgabe'
        : `${naechste.nochKarten} Aufgaben`}
      , die sicher sitzen, dann bekommst du die Ballons{' '}
      <strong>{naechste.set.name}</strong>.
    </p>
  )
}

/**
 * Wechselnde Ueberschrift auf der Abschlussseite.
 *
 * Immer dasselbe "Geschafft!" nutzt sich nach der zwoelften Runde ab. Die
 * Auswahl haengt am Ergebnis und nicht am Zufall: So sagt die Ueberschrift
 * jedes Mal etwas Zutreffendes ueber genau diese Runde.
 */
function abschlussTitel(e: Rundenergebnis): string {
  const anteil = e.anzahlAufgaben ? e.anzahlGewusst / e.anzahlAufgaben : 0

  if (e.laengsteKette >= 15) return 'Was für eine Serie!'
  if (anteil >= 0.9) return 'Stark gerechnet!'
  if (e.laengsteKette >= 10) return 'Lange Kette!'
  if (anteil >= 0.7) return 'Gut gemacht!'
  if (anteil >= 0.4) return 'Geschafft!'
  return 'Fertig für heute!'
}
